import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
    getAllUsers,
    getPendingInvitations,
    inviteUser,
    cancelInvitation,
    resendInvitation,
    deleteUser,
    formatPhoneDisplay,
    formatPhoneInput,
    validateAustralianPhone
} from "../../lib/supabase/users";

import { MdVerified, MdEmail, MdRefresh, MdClose } from 'react-icons/md';
import ConfirmationPopup from "../popup/ConfirmationPopup";
import { hasPermission } from "../../lib/permissions";

const Users = () => {
    const { userProfile, company } = useAuth();
    const [users, setUsers] = useState({
        admins: [],
        designers: [],
        drivers: [],
        estimators: []
    });
    const [pendingInvitations, setPendingInvitations] = useState([]);
    const [activeCategory, setActiveCategory] = useState("admins");
    const [showAddUserPopup, setShowAddUserPopup] = useState(false);
    const [showInvitationsPopup, setShowInvitationsPopup] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        username: "",
        email: "",
        role: "",
        password: "",
        phone: "",
        userType: "admin"
    });

    const navigate = useNavigate();

    const categories = [
        { key: "admins", label: "Admins", color: "bg-blue-500", border: "border-blue-500" },
        { key: "designers", label: "Designers", color: "bg-green-500", border: "border-green-500" },
        { key: "estimators", label: "Estimators", color: "bg-yellow-500", border: "border-yellow-500" },
        { key: "drivers", label: "Drivers", color: "bg-red-500", border: "border-red-500" }
    ];

    useEffect(() => {
        if (company?.id) {
            fetchAllData();
        }
    }, [company]);

    const fetchAllData = async () => {
        try {
            setLoading(true);
            const [usersData, invitationsData] = await Promise.all([
                getAllUsers(company.id),
                getPendingInvitations(company.id)
            ]);
            setUsers(usersData);
            setPendingInvitations(invitationsData);
        } catch (error) {
            console.error("Error fetching data:", error);
            alert("Failed to load users: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();

        // Validate email
        if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            alert("Please enter a valid email address");
            return;
        }

        // Validate phone if provided
        if (formData.phone && !validateAustralianPhone(formData.phone)) {
            alert("Please enter a valid Australian phone number");
            return;
        }

        try {
            setLoading(true);
            const result = await inviteUser(
                formData,
                company.id,
                userProfile.id,
                userProfile.username || userProfile.full_name
            );

            alert(result.message + "\n\nInvitation URL: " + result.inviteUrl);

            setFormData({
                username: "",
                email: "",
                role: "",
                password: "",
                phone: "",
                userType: "admin"
            });
            setShowAddUserPopup(false);
            await fetchAllData();
        } catch (error) {
            console.error("Error inviting user:", error);
            alert("Failed to send invitation: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = (user) => {
        setPendingAction({
            type: 'delete',
            data: user,
            message: `Are you sure you want to delete ${user.username}${user.position ? ` (${user.position})` : ''}?`
        });
        setShowConfirmation(true);
    };

    const handleCancelInvitation = (invitation) => {
        setPendingAction({
            type: 'cancel_invitation',
            data: invitation,
            message: `Are you sure you want to cancel the invitation for ${invitation.email}?`
        });
        setShowConfirmation(true);
    };

    const handleResendInvitation = async (invitationId) => {
        try {
            setLoading(true);
            const result = await resendInvitation(invitationId);
            alert(result.message);
            await fetchAllData();
        } catch (error) {
            console.error("Error resending invitation:", error);
            alert("Failed to resend invitation: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmAction = async () => {
        try {
            setLoading(true);

            if (pendingAction.type === 'delete') {
                // Prevent self-deletion
                if (pendingAction.data.id === userProfile.id) {
                    alert("Cannot delete your own account");
                    return;
                }

                // Prevent deletion of super_admin users
                if (pendingAction.data.role === 'super_admin') {
                    alert("Cannot delete super admin users");
                    return;
                }

                await deleteUser(pendingAction.data.id);
                alert("User deleted successfully");
            } else if (pendingAction.type === 'cancel_invitation') {
                await cancelInvitation(pendingAction.data.id);
                alert("Invitation cancelled successfully");
            }

            await fetchAllData();
        } catch (error) {
            console.error("Error performing action:", error);
            alert("Action failed: " + error.message);
        } finally {
            setLoading(false);
            setShowConfirmation(false);
            setPendingAction(null);
        }
    };

    const handleCancelAction = () => {
        setShowConfirmation(false);
        setPendingAction(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "phone") {
            const formattedPhone = formatPhoneInput(value);
            setFormData((prevState) => ({
                ...prevState,
                [name]: formattedPhone,
            }));
        } else {
            setFormData((prevState) => ({
                ...prevState,
                [name]: value,
            }));
        }
    };

    const renderUserCard = (user, userType) => {
        const isOnline = false; // You can integrate WebSocket for online status

        return (
            <div
                key={user.id}
                className={`bg-white dark:bg-zinc-800 rounded-lg p-3 sm:p-4 shadow-md border-l-4 ${categories.find(c => c.key === userType)?.border || 'border-gray-500'
                    } w-full`}
            >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-2 sm:gap-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <div
                                className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-gray-300'}`}
                                title={isOnline ? 'Online' : 'Offline'}
                            />
                            <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
                                {user?.full_name || user.username}
                            </h3>
                            <span
                                className={`text-xs px-2 py-1 rounded-full font-medium ${categories.find(c => c.key === userType)?.color || 'bg-gray-500'
                                    } text-white capitalize`}
                            >
                                {userType !== "admins" ? `${userType.slice(0, -1)}` : `${user?.position || "admin"}`}
                            </span>
                            {user.id === userProfile.id && (
                                <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                                    You
                                </span>
                            )}
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">
                            {user.role === 'super_admin' && (
                                <div className="flex items-center gap-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded-full text-xs">
                                    <MdVerified className="w-3 h-3" />
                                    <span className="font-medium hidden sm:inline">Super Admin</span>
                                    <span className="font-medium sm:hidden">Super</span>
                                </div>
                            )}
                            {user.phone ? (
                                <p
                                    className="text-xs sm:text-sm text-gray-600 dark:text-gray-400"
                                >
                                    +61 {formatPhoneDisplay(user.phone)}
                                </p>
                            ) : (
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                    No phone
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="flex-shrink-0 self-end sm:self-auto">
                        {userProfile?.role === 'super_admin' && user.role !== 'super_admin' && user.id !== userProfile.id && (
                            <button
                                onClick={() => handleDeleteUser(user)}
                                className="px-2 sm:px-3 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors duration-200"
                            >
                                <span className="hidden sm:inline">Delete</span>
                                <span className="sm:hidden">Del</span>
                            </button>
                        )}
                    </div>
                </div>
            </div >
        );
    };

    const renderInvitationCard = (invitation) => {
        const isExpired = new Date(invitation.expires_at) < new Date();

        return (
            <div
                key={invitation.id}
                className="bg-white dark:bg-zinc-800 rounded-lg p-3 sm:p-4 shadow-md border-l-4 border-orange-500 w-full"
            >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-2">
                    <div className="flex flex-col gap-2 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <MdEmail className="w-4 h-4 text-orange-500" />
                            <span className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
                                {invitation.email}
                            </span>
                            <span className="text-xs px-2 py-1 rounded-full font-medium bg-orange-500 text-white capitalize">
                                {invitation.role}
                            </span>
                            {isExpired && (
                                <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                                    Expired
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                            <span>Username: {invitation.username}</span>
                            {invitation.phone && <span>• +61 {formatPhoneDisplay(invitation.phone)}</span>}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            Invited by {invitation.invited_by_name} • Expires{' '}
                            {new Date(invitation.expires_at).toLocaleDateString()}
                        </div>
                    </div>

                    <div className="flex gap-2 self-end sm:self-auto">
                        <button
                            onClick={() => handleResendInvitation(invitation.id)}
                            className="px-2 sm:px-3 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors duration-200 flex items-center gap-1"
                            title="Resend invitation"
                        >
                            <MdRefresh className="w-3 h-3" />
                            <span className="hidden sm:inline">Resend</span>
                        </button>
                        <button
                            onClick={() => handleCancelInvitation(invitation)}
                            className="px-2 sm:px-3 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors duration-200 flex items-center gap-1"
                            title="Cancel invitation"
                        >
                            <MdClose className="w-3 h-3" />
                            <span className="hidden sm:inline">Cancel</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    if (loading && !users.admins.length) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <>
            {/* Page Header */}
            <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                    Users
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                    See {hasPermission(userProfile?.role, ["super_admin", "admin"]) && " & manage"} team members {hasPermission(userProfile?.role, ["super_admin", "admin"]) ? " and pending invitations" : " and their contacts"}
                </p>
            </div>

            {/* Current User Card */}
            {userProfile && (
                <div className="mb-4 sm:mb-6">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2 sm:mb-3">
                        Current User
                    </h2>
                    <div className="bg-white dark:bg-zinc-800 rounded-lg p-3 sm:p-4 shadow-md border-l-4 border-blue-500 ring-2 ring-blue-400">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-2 sm:gap-0">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="w-2 h-2 rounded-full bg-green-500" title="Online" />
                                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
                                        {userProfile.full_name || userProfile.username}
                                    </h3>
                                    <span className={`text-xs px-2 py-1 rounded-full font-medium text-white capitalize ${categories.find(c => c.key === userProfile?.role + "s")?.color}`}>
                                        {userProfile.role}
                                    </span>
                                    <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                                        You
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    {userProfile.role === 'super_admin' && (
                                        <div className="flex items-center gap-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded-full text-xs">
                                            <MdVerified className="w-3 h-3" />
                                            <span className="font-medium">Super Admin</span>
                                        </div>
                                    )}
                                    <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                        {userProfile.phone ? `+61 ${formatPhoneDisplay(userProfile.phone)}` : 'No phone'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            {userProfile?.role?.includes('admin') && (
                <div className="my-3 flex flex-col sm:flex-row gap-2 sm:gap-3 items-stretch sm:items-center justify-end">
                    {pendingInvitations.length > 0 && (
                        <button
                            onClick={() => setShowInvitationsPopup(true)}
                            className="bg-orange-500 hover:bg-orange-600 text-white px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base rounded-lg font-semibold shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
                        >
                            <MdEmail className="w-5 h-5" />
                            <span>Pending Invitations ({pendingInvitations.length})</span>
                        </button>
                    )}
                    <button
                        onClick={() => setShowAddUserPopup(true)}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 sm:px-12 py-2 sm:py-3 text-sm sm:text-base rounded-lg font-semibold shadow-lg transition-all duration-200"
                    >
                        <span className="hidden sm:inline">+ Invite New User</span>
                        <span className="sm:hidden">+ Invite User</span>
                    </button>
                </div>
            )}

            {/* Main Users Container */}
            <div className="bg-white dark:bg-zinc-800 rounded-xl shadow-lg overflow-hidden">
                {/* Category Buttons */}
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1 sm:gap-2 p-2 sm:p-4 bg-gray-50 dark:bg-zinc-900 border-b dark:border-zinc-700">
                    {categories.map((category) => (
                        <button
                            key={category.key}
                            onClick={() => setActiveCategory(category.key)}
                            className={`w-full sm:w-auto px-3 sm:px-4 py-2 sm:py-2 text-sm sm:text-sm rounded-lg font-medium transition-all duration-200 ${activeCategory === category.key
                                ? `${category.color} text-white shadow-md`
                                : 'bg-white dark:bg-zinc-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-zinc-600'
                                }`}
                        >
                            <span className="flex justify-between items-center">
                                <span>{category.label}</span>
                                <span className="ml-1">({users[category.key]?.length || 0})</span>
                            </span>
                        </button>
                    ))}
                </div>

                {/* Users Grid */}
                <div className="p-2 sm:p-4">
                    {users[activeCategory]?.length > 0 ? (
                        <div className="space-y-2 sm:space-y-3">
                            {users[activeCategory].map((user) => renderUserCard(user, activeCategory))}
                        </div>
                    ) : (
                        <div className="text-center py-8 sm:py-12">
                            <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base">
                                No {activeCategory} found
                            </p>
                        </div>
                    )}
                </div>
            </div>


            {/* Add User Popup */}
            {
                showAddUserPopup && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
                        <div className="bg-white dark:bg-zinc-800 rounded-xl shadow-2xl w-full max-w-md max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
                            <div className="p-4 sm:p-6">
                                <div className="flex justify-between items-center mb-4 sm:mb-6">
                                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                                        Invite New User
                                    </h2>
                                    <button
                                        onClick={() => setShowAddUserPopup(false)}
                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl sm:text-2xl"
                                    >
                                        ×
                                    </button>
                                </div>

                                <form onSubmit={handleAddUser} className="space-y-3 sm:space-y-4">
                                    {/* User Type */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                            User Type
                                        </label>
                                        <select
                                            name="userType"
                                            value={formData.userType}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-zinc-600 rounded-lg dark:bg-zinc-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            required
                                        >
                                            <option value="admin">Admin</option>
                                            <option value="designer">Designer</option>
                                            <option value="estimator">Estimator</option>
                                            <option value="driver">Driver</option>
                                        </select>
                                    </div>

                                    {/* Email */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-zinc-600 rounded-lg dark:bg-zinc-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="user@example.com"
                                            required
                                        />
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            An invitation will be sent to this email
                                        </p>
                                    </div>

                                    {/* Username */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                            Username
                                        </label>
                                        <input
                                            type="text"
                                            name="username"
                                            value={formData.username}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-zinc-600 rounded-lg dark:bg-zinc-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Enter username"
                                            required
                                        />
                                    </div>

                                    {/* Role (for admins only) */}
                                    {formData.userType === "admin" && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                                Position/Role (Optional)
                                            </label>
                                            <input
                                                type="text"
                                                name="role"
                                                value={formData.role}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-zinc-600 rounded-lg dark:bg-zinc-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="e.g., Manager, Supervisor"
                                            />
                                        </div>
                                    )}

                                    {/* Phone */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                            Phone Number (Optional)
                                        </label>
                                        <div className="flex">
                                            <span className="inline-flex items-center px-3 py-2 text-sm sm:text-base bg-gray-100 dark:bg-zinc-700 border border-r-0 border-gray-300 dark:border-zinc-600 rounded-l-lg text-gray-700 dark:text-gray-300">
                                                +61
                                            </span>
                                            <input
                                                type="text"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                className="flex-1 px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-zinc-600 rounded-r-lg dark:bg-zinc-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="433 177 867"
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            Enter mobile (04xx xxx xxx) or landline (02/03/07/08xx xxx xxx)
                                        </p>
                                    </div>

                                    {/* Info Box */}
                                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                                        <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-200">
                                            <strong>Note:</strong> An invitation email will be sent to the user. They will
                                            create their own password when they accept the invitation.
                                        </p>
                                    </div>

                                    {/* Buttons */}
                                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-3 sm:pt-4">
                                        <button
                                            type="button"
                                            onClick={() => setShowAddUserPopup(false)}
                                            className="w-full sm:flex-1 px-4 py-2 text-sm sm:text-base border border-gray-300 dark:border-zinc-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors duration-200"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full sm:flex-1 px-4 py-2 text-sm sm:text-base bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {loading ? 'Sending...' : 'Send Invitation'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Pending Invitations Popup */}
            {
                showInvitationsPopup && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
                        <div className="bg-white dark:bg-zinc-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
                            <div className="p-4 sm:p-6 border-b dark:border-zinc-700">
                                <div className="flex justify-between items-center">
                                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                        <MdEmail className="w-5 h-5 text-orange-500" />
                                        Pending Invitations ({pendingInvitations.length})
                                    </h2>
                                    <button
                                        onClick={() => setShowInvitationsPopup(false)}
                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl sm:text-2xl"
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                                {pendingInvitations.length > 0 ? (
                                    <div className="space-y-3">
                                        {pendingInvitations.map((invitation) => renderInvitationCard(invitation))}
                                    </div>
                                ) : (
                                    <div className="text-center py-12">
                                        <MdEmail className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                                        <p className="text-gray-500 dark:text-gray-400">No pending invitations</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Confirmation Popup */}
            <ConfirmationPopup
                isOpen={showConfirmation}
                title={pendingAction?.type === 'delete' ? 'Confirm Delete User' : 'Confirm Cancel Invitation'}
                message={pendingAction?.message || ''}
                confirmText={pendingAction?.type === 'delete' ? 'Delete User' : 'Cancel Invitation'}
                cancelText="Cancel"
                onConfirm={handleConfirmAction}
                onCancel={handleCancelAction}
            />
        </>
    );
};

export default Users;